import {token} from "@atlaskit/tokens";
import StatusErrorIcon from "@atlaskit/icon/core/status-error";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {createPortal} from "react-dom";
import {useEffect, useState} from "react";
import {useCheckMobile} from "~/hooks/useCheckMobile";
import {BottomDrawer} from "~/components/BottomDrawer";

function useMobileSheetMidHeight(): number {
    const [height, setHeight] = useState(400);

    useEffect(() => {
        const updateHeight = () => {
            setHeight(Math.round(window.innerHeight * 0.5));
        };
        updateHeight();
        window.addEventListener("resize", updateHeight);
        return () => {
            window.removeEventListener("resize", updateHeight);
        };
    }, []);

    return height;
}

const DISCARD_TITLE = "Discard unsaved changes?";
const DISCARD_BODY = "Your recent edits haven't been saved. Are you sure you want to discard them and close this panel?";
/** Above Mantine `Modal` (200) and default `BottomDrawer` (60) so confirm shows over row-engagement panels. */
const DISCARD_UNSAVED_CHANGES_Z_INDEX = 10000;

/**
 * Confirmation shown when a user tries to leave a form/panel that has unsaved
 * edits. Desktop renders a centered overlay; mobile renders a `BottomDrawer`.
 *
 * This component is presentational only — the open state and the
 * cancel/discard handlers are owned by `useUnsavedChangesGuard`.
 */
export function DiscardUnsavedChangesModal({
    isOpen,
    onCancel,
    onDiscard,
}: {
    isOpen: boolean;
    /** Keep editing — dismiss the confirmation and stay on the form. */
    onCancel: () => void;
    /** Throw away edits — proceed with the pending close/navigation. */
    onDiscard: () => void;
}) {
    const isMobile = useCheckMobile();
    const mobileSheetMidHeight = useMobileSheetMidHeight();

    if (isOpen !== true) {
        return null;
    }

    const body = (
        <div className="grid grid-flow-row gap-ds-space-200">
            <p
                style={{
                    margin: isMobile === true ? 8 : 0,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: token("color.text.subtle", "#626F86"),
                }}
            >
                {DISCARD_BODY}
            </p>

            <div className="grid grid-flow-col auto-cols-max justify-end items-center gap-ds-space-100">
                <AnankeButton
                    type="button"
                    appearance="subtle"
                    onClick={onCancel}
                >
                    Cancel
                </AnankeButton>
                <AnankeButton
                    type="button"
                    appearance="danger"
                    onClick={onDiscard}
                >
                    Discard
                </AnankeButton>
            </div>
        </div>
    );

    if (isMobile === true) {
        return (
            <BottomDrawer
                open={isOpen}
                zIndex={DISCARD_UNSAVED_CHANGES_Z_INDEX}
                setOpen={(open) => {
                    if (open === false) {
                        onCancel();
                    }
                }}
                initialOpenHeight={mobileSheetMidHeight}
                compactLayout={true}
                lockContentScroll={true}
                headerBottomBorder={true}
                title={
                    <div className="grid grid-flow-col auto-cols-max items-center gap-ds-space-100">
                        <StatusErrorIcon
                            label=""
                            color={token("color.icon.danger", "#C9372C")}
                        />
                        <span
                            style={{
                                fontSize: "16px",
                                fontWeight: 600,
                                color: token("color.text", "#172B4D"),
                                fontFamily: "Inter",
                            }}
                        >
                            {DISCARD_TITLE}
                        </span>
                    </div>
                }
            >
                <div className="pb-ds-space-200">{body}</div>
            </BottomDrawer>
        );
    }

    if (typeof document === "undefined") {
        return null;
    }

    return createPortal(
        <>
            {/* Backdrop */}
            <div
                onClick={onCancel}
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: DISCARD_UNSAVED_CHANGES_Z_INDEX,
                    backgroundColor: token("color.blanket", "rgba(9, 30, 66, 0.54)"),
                }}
            />

            {/* Modal */}
            <div
                style={{
                    position: "fixed",
                    inset: 0,
                    zIndex: DISCARD_UNSAVED_CHANGES_Z_INDEX + 1,
                    display: "grid",
                    placeItems: "center",
                    pointerEvents: "none",
                }}
            >
                <div
                    style={{
                        width: "400px",
                        maxWidth: "calc(100vw - 32px)",
                        background: token("elevation.surface.overlay", "#FFFFFF"),
                        borderRadius: "8px",
                        boxShadow: token("elevation.shadow.overlay", "0 8px 16px rgba(0,0,0,0.15)"),
                        padding: "24px",
                        display: "grid",
                        gridAutoFlow: "row",
                        gap: "16px",
                        pointerEvents: "auto",
                    }}
                >
                    {/* Header */}
                    <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-ds-space-100">
                        <StatusErrorIcon
                            label=""
                            color={token("color.icon.danger", "#C9372C")}
                        />
                        <h2
                            style={{
                                margin: 0,
                                fontSize: "20px",
                                fontWeight: 600,
                                color: token("color.text", "#172B4D"),
                                fontFamily: "Inter, sans-serif",
                            }}
                        >
                            {DISCARD_TITLE}
                        </h2>
                    </div>

                    {body}
                </div>
            </div>
        </>,
        document.body,
    );
}
